import { useEffect, useMemo } from 'react';
import { useTitleMusic } from '../contexts/TitleMusicContext';
import type { SeasonInfo } from '../types/season';
import {
  filterSeasonCases,
  sortCasesBySeasonAndEpisode,
} from '../utils/caseSorting';
import { useCaseLibraryVisibility } from './useCaseLibraryVisibility';
import { useCaseSelection } from './useCaseSelection';

/**
 * Custom hook that consolidates common logic for case discovery/selection pages
 * @returns visibleCases, seasons, loading, and error state
 */
export const useCaseDiscoverySetup = () => {
  const { caseInfos, loading, error } = useCaseSelection();
  const { getVisibleCases } = useCaseLibraryVisibility();
  const { startTitleMusic } = useTitleMusic();

  // Get visible cases from the library
  const allVisibleCases = getVisibleCases(caseInfos);

  // Filter to only cases with seasonId for discover page
  const seasonFilteredCases = useMemo(
    () => filterSeasonCases(allVisibleCases),
    [allVisibleCases],
  );

  // Extract unique seasons from cases that have season data
  // TODO: Load seasons from data service when available
  const seasons = useMemo((): SeasonInfo[] => {
    const seasonMap = new Map<string, SeasonInfo>();

    seasonFilteredCases.forEach((caseInfo) => {
      if (caseInfo.seasonId && !seasonMap.has(caseInfo.seasonId)) {
        // Create season info from case data
        // In the future, this should come from a dedicated seasons endpoint
        seasonMap.set(caseInfo.seasonId, {
          seasonId: caseInfo.seasonId,
          seasonNumber: seasonMap.size + 1, // Temporary numbering
          title: `Season ${seasonMap.size + 1}`,
          requiredSeasonId: undefined, // Will be populated from data service
          releaseDate: undefined,
        });
      }
    });

    return Array.from(seasonMap.values()).sort(
      (a, b) => a.seasonNumber - b.seasonNumber,
    );
  }, [seasonFilteredCases]);

  // Sort cases by season and episode number (S1E1, S1E2, S1E3, etc.)
  const visibleCases = useMemo(
    () => sortCasesBySeasonAndEpisode(seasonFilteredCases, seasons),
    [seasonFilteredCases, seasons],
  );

  // Check if we have season data
  const hasSeasonData = seasons.length > 0;

  // Start title music when component mounts
  useEffect(() => {
    startTitleMusic();
  }, [startTitleMusic]);

  return {
    visibleCases,
    seasons,
    hasSeasonData,
    loading,
    error,
  };
};
